import { ChapterResponse } from '@goldcast/api/content';
import React, { useCallback, useMemo } from 'react';
import ChapterActionMenu from './ChapterActionMenu';
import { MINIMUM_CHAPTER_TIME_DIFFERENCE } from '../utils';
import { getAbsolutePlayerTime } from '../timeUtils';
import { convertSecondsToTimeString } from '@/player-config/src/utils/timeUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { roundToNDecimalPlaces } from '@/player-config/src/utils/mathUtils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';

export interface IndividualChapterPropInterface extends ChapterResponse {
  end_time: number;
}

export interface ChapterStartAndEndTime {
  start_time: number;
  end_time: number;
}

export default function IndividualChapterItem({
  chapter,
  clipStartTime,
  chapterStartAndEndTime,
  onMouseDown,
  previousChapter,
  nextChapter,
  isDraggingChapter,
  isDragging
}: {
  chapter: IndividualChapterPropInterface;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>, chapterId: string, maxOffset: number, minOffset: number) => void;
  previousChapter: IndividualChapterPropInterface | null;
  nextChapter: IndividualChapterPropInterface | null;
  isDraggingChapter: boolean;
  isDragging: boolean;
  chapterStartAndEndTime: ChapterStartAndEndTime;
  clipStartTime: number;
}) {
  const { fps, clipData } = useClipsContext();
  const isSplitsEnabled = useMemo(() => isSplitsInTimelineEnabled(clipData), [clipData]);
  const chapterAbsoluteStartTime = getAbsolutePlayerTime(chapter.start_time, clipData);
  const chapterAbsoluteEndTime = getAbsolutePlayerTime(chapter.end_time, clipData);
  const chapterWidth = isSplitsEnabled
    ? (chapterAbsoluteEndTime - chapterAbsoluteStartTime) * fps
    : (chapter.end_time - chapter.start_time) * fps;
  const chapterStart = isSplitsEnabled ? chapterAbsoluteStartTime * fps : chapter.start_time * fps;
  const isFirstChapter = !previousChapter;
  const isLastChapter = !nextChapter;
  const newTimeAfterDrag = chapter.start_time;

  // Let user drag the first chapter if its not at the start of the clip
  const canDrag =
    !isFirstChapter || roundToNDecimalPlaces(chapter.start_time, 2) > roundToNDecimalPlaces(clipStartTime, 2);

  const onHandleDrag = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const maxOffset = isFirstChapter
        ? 0
        : Math.max(
            0,
            (nextChapter ? nextChapter.start_time - MINIMUM_CHAPTER_TIME_DIFFERENCE : chapter.end_time) -
              chapter.start_time
          );
      const minOffset = previousChapter
        ? Math.max(0, chapter.start_time - previousChapter.start_time - MINIMUM_CHAPTER_TIME_DIFFERENCE)
        : chapter.start_time;
      onMouseDown(e, chapter.id, maxOffset, -minOffset);
    },
    [isFirstChapter, nextChapter, chapter.end_time, chapter.start_time, chapter.id, previousChapter, onMouseDown]
  );

  return (
    <div
      className={`${isLastChapter ? '' : 'pr-[1.5px]'} ${isFirstChapter ? '' : 'pl-[1.5px]'} absolute`}
      style={{
        width: `calc(${chapterWidth}px * var(--timeline-zoom))`,
        left: `calc(${chapterStart}px * var(--timeline-zoom))`
      }}
    >
      <div className="max-w-full overflow-hidden rounded-[3px]">
        <div
          className={`flex h-[30px] w-full items-start justify-center overflow-hidden bg-slate-100 text-slate-600 ${
            isDraggingChapter || isDragging ? '' : 'hover:bg-slate-200'
          }`}
        >
          <ChapterActionMenu
            chapter={chapter}
            chapterStartAndEndTime={chapterStartAndEndTime}
            isDragging={isDragging}
          />
        </div>
      </div>
      {canDrag && (!isDragging || isDraggingChapter) && (
        <div
          className={`absolute inset-y-[3px] left-[-0.5px] z-10 w-[3px] cursor-ew-resize opacity-0 hover:opacity-100 ${
            isDraggingChapter ? 'opacity-100' : 'opacity-0'
          }`}
          onMouseDown={onHandleDrag}
        >
          <div className="m-auto h-full w-[2px] -translate-x-1/2 bg-zinc-900"></div>
        </div>
      )}
      {isDraggingChapter && (
        <div className="absolute -bottom-1 z-20 h-[21px] w-fit -translate-x-1/2 translate-y-full rounded-sm border border-zinc-900 bg-zinc-900 px-1.5 py-0.5 text-2xs leading-[15px] text-white">
          {convertSecondsToTimeString(newTimeAfterDrag, 'HH:mm:ss:S')}
        </div>
      )}
    </div>
  );
}
